import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  LayoutDashboard,
  Building2,
  CheckCircle,
  FileText,
  Users,
  Euro,
  Shield,
  Zap,
  BarChart3,
  ArrowRight,
  AlertTriangle,
  Receipt
} from 'lucide-react';

const previewTabs = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, img: '/screenshots/dashboard.jpg', caption: 'Alle Kennzahlen auf einen Blick' },
  { id: 'properties', label: 'Immobilien', icon: Building2, img: '/screenshots/properties.jpg', caption: 'Übersicht aller Objekte' },
  { id: 'costs', label: 'Kosten & Belege', icon: Receipt, img: '/screenshots/costs.jpg', caption: 'Betriebskosten mit Beleg-Upload' },
  { id: 'billings', label: 'Abrechnungen', icon: FileText, img: '/screenshots/billings.jpg', caption: 'Automatische Berechnung & PDF-Export' },
  { id: 'damages', label: 'Schäden', icon: AlertTriangle, img: '/screenshots/damages.jpg', caption: 'Schadensmanagement mit Status-Tracking' },
];

function AppPreviewTabs() {
  const [active, setActive] = useState('dashboard');
  const current = previewTabs.find((t) => t.id === active);

  return (
    <div data-testid="app-preview-tabs">
      <div className="flex flex-wrap justify-center gap-2 mb-6">
        {previewTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = tab.id === active;
          return (
            <button
              key={tab.id}
              data-testid={`preview-tab-${tab.id}`}
              onClick={() => setActive(tab.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                isActive
                  ? 'bg-slate-900 text-white shadow-md'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300 hover:text-slate-900'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="relative max-w-6xl mx-auto">
        {/* Browser chrome */}
        <div className="bg-slate-200 rounded-t-xl px-4 py-3 flex items-center gap-2 border border-slate-300 border-b-0">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-400"></div>
            <div className="w-3 h-3 rounded-full bg-amber-400"></div>
            <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
          </div>
          <div className="ml-4 px-3 py-1 bg-white/70 rounded-md text-xs text-slate-500 font-mono">
            app.kivera.de/{current.id === 'dashboard' ? '' : current.id}
          </div>
        </div>
        <div className="bg-white rounded-b-xl border border-slate-300 overflow-hidden shadow-2xl">
          <img
            src={current.img}
            alt={`Kivera ${current.label} – ${current.caption}`}
            data-testid={`preview-image-${current.id}`}
            className="w-full h-auto block"
            loading="lazy"
          />
        </div>
        <p className="text-center text-sm text-slate-500 mt-4">{current.caption}</p>
      </div>
    </div>
  );
}

const features = [
  {
    icon: Building2,
    title: 'Immobilienverwaltung',
    description: 'Verwalten Sie alle Ihre Objekte, Einheiten und Vermieter zentral an einem Ort.'
  },
  {
    icon: Users,
    title: 'Mieterverwaltung',
    description: 'Behalten Sie den Überblick über Mietverhältnisse, Einzugsdaten und Vorauszahlungen.'
  },
  {
    icon: Euro,
    title: 'Kostenerfassung',
    description: 'Erfassen Sie alle Betriebskosten mit automatischer Kategorisierung nach BetrKV.'
  },
  {
    icon: FileText,
    title: 'Automatische Abrechnung',
    description: 'Erstellen Sie rechtssichere Nebenkostenabrechnungen per Knopfdruck.'
  },
  {
    icon: BarChart3,
    title: 'Übersichtliches Dashboard',
    description: 'Alle wichtigen Kennzahlen auf einen Blick – Kosten, Nachzahlungen, offene Abrechnungen.'
  },
  {
    icon: Shield,
    title: 'Schadensmanagement',
    description: 'Erfassen und verfolgen Sie Schäden, dokumentieren Sie Reparaturen und behalten Sie Kosten im Blick.'
  },
  {
    icon: Users,
    title: 'Mieterzugang',
    description: 'Geben Sie Ihren Mietern Zugang zu Abrechnungen, Dokumenten und Schadensmeldungen.'
  },
  {
    icon: Zap,
    title: 'DSGVO-konform',
    description: 'Ihre Daten werden sicher in Deutschland gehostet und sind DSGVO-konform geschützt.'
  }
];

const pricingPlans = [
  {
    name: 'Starter',
    description: 'Für den Einstieg',
    price: '9',
    period: 'pro Monat',
    highlight: false,
    features: [
      'Bis zu 3 Immobilien',
      'Bis zu 10 Einheiten',
      'Unbegrenzte Mieter',
      'Nebenkostenabrechnung',
      'PDF-Export',
      'Schadensmanagement',
      'E-Mail Support'
    ],
    cta: 'Jetzt starten',
    ctaVariant: 'outline'
  },
  {
    name: 'Professional',
    description: 'Für wachsende Portfolios',
    price: '19',
    period: 'pro Monat',
    highlight: true,
    badge: 'Beliebt',
    features: [
      'Bis zu 10 Immobilien',
      'Bis zu 50 Einheiten',
      'Unbegrenzte Mieter',
      'Nebenkostenabrechnung',
      'PDF-Export',
      'Schadensmanagement',
      'Mieterzugang',
      'Prioritäts-Support',
      'Dokumenten-Upload',
      'E-Mail-Versand'
    ],
    cta: 'Jetzt starten',
    ctaVariant: 'default'
  },
  {
    name: 'Business',
    description: 'Für Hausverwaltungen',
    price: '49',
    period: 'pro Monat',
    highlight: false,
    features: [
      'Unbegrenzte Immobilien',
      'Unbegrenzte Einheiten',
      'Unbegrenzte Mieter',
      'Nebenkostenabrechnung',
      'PDF-Export',
      'Schadensmanagement',
      'Mieterzugang',
      'Premium-Support',
      'Dokumenten-Upload',
      'E-Mail-Versand',
      'Mehrbenutzer-Zugang',
      'API-Zugang'
    ],
    cta: 'Kontakt aufnehmen',
    ctaVariant: 'outline'
  }
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Building2 className="h-8 w-8 text-emerald-600" />
              <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Kivera
              </span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 text-sm font-medium">
                Funktionen
              </a>
              <a href="#preview" className="text-slate-600 hover:text-slate-900 text-sm font-medium">
                Vorschau
              </a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 text-sm font-medium">
                Preise
              </a>
              <Link to="/roadmap" data-testid="nav-roadmap-link" className="text-slate-600 hover:text-slate-900 text-sm font-medium">
                Roadmap
              </Link>
              <Link to="/register">
                <Button variant="ghost" data-testid="nav-login-button">Registrieren</Button>
              </Link>
              <Link to="/mieter/login">
                <Button variant="ghost" data-testid="nav-mieter-login-button" className="text-slate-600">Mieterportal</Button>
              </Link>
              <Link to="/login">
                <Button data-testid="nav-register-button" className="bg-emerald-600 hover:bg-emerald-700">
                  Anmelden
                </Button>
              </Link>
            </div>
            <div className="md:hidden flex items-center gap-2">
              <Link to="/login">
                <Button variant="ghost" size="sm">Anmelden</Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">Starten</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                <Zap className="h-3 w-3 mr-1" />
                Neu: Automatische Abrechnungserstellung
              </Badge>
              <h1 
                className="text-3xl sm:text-4xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Immobilienverwaltung,{' '}
                <span className="text-emerald-600">die mitdenkt.</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Kivera macht die Nebenkostenabrechnung einfach. Verwalten Sie Ihre Immobilien, 
                erfassen Sie Kosten und erstellen Sie rechtssichere Abrechnungen – alles in einer 
                intuitiven Anwendung.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button 
                    size="lg" 
                    className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
                    data-testid="hero-cta-button"
                  >
                    Mit Einladung registrieren
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <a href="#features">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto">
                    Mehr erfahren
                  </Button>
                </a>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  Einladungscode erforderlich
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  14 Tage kostenlos testen
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-emerald-50 to-slate-100 rounded-2xl p-4 shadow-2xl">
                <img 
                  src="/hero-haus.jpg"
                  alt="Kivera – Immobilienverwaltung"
                  className="rounded-xl shadow-lg w-full"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Euro className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Ersparnis pro Jahr</p>
                    <p className="font-bold text-slate-900">Ø 2.400 €</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar – Early Access */}
      <section className="py-10 bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 mb-3" data-testid="early-access-badge">
              Early Access · April 2026
            </Badge>
            <p className="text-slate-600 max-w-2xl mx-auto text-sm sm:text-base">
              Kivera wird gerade aktiv entwickelt. Werden Sie einer der ersten Vermieter,
              die ihre Nebenkostenabrechnung von Grund auf neu gedacht erleben.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2 justify-center text-sm text-slate-700">
              <Shield className="h-4 w-4 text-emerald-600 flex-shrink-0" />
              <span>BetrKV-konform</span>
            </div>
            <div className="flex items-center gap-2 justify-center text-sm text-slate-700">
              <CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0" />
              <span>DSGVO &amp; Hosting in DE</span>
            </div>
            <div className="flex items-center gap-2 justify-center text-sm text-slate-700">
              <Building2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
              <span>Made in Dresden</span>
            </div>
            <div className="flex items-center gap-2 justify-center text-sm text-slate-700">
              <Zap className="h-4 w-4 text-emerald-600 flex-shrink-0" />
              <span>Datenexport jederzeit</span>
            </div>
          </div>
        </div>
      </section>

      {/* App Preview / Screenshots */}
      <section id="preview" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Live-Vorschau</Badge>
            <h2
              className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              So sieht Kivera in Aktion aus
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Aufgeräumte Oberfläche, deutsche Sprache, alle relevanten Kennzahlen auf einen Blick.
            </p>
          </div>

          <AppPreviewTabs />
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Alles, was Sie für die Nebenkostenabrechnung brauchen
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Kivera vereinfacht die komplette Immobilienverwaltung – von der Kostenerfassung 
              bis zur fertigen Abrechnung.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-emerald-600" />
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              In 3 Schritten zur Abrechnung
            </h2>
            <p className="text-lg text-slate-600">
              So einfach war Nebenkostenabrechnung noch nie.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Daten anlegen', desc: 'Erfassen Sie Ihre Immobilien, Einheiten und Mieter in wenigen Minuten.' },
              { step: '2', title: 'Kosten eintragen', desc: 'Fügen Sie Ihre Betriebskosten hinzu – kategorisiert nach BetrKV.' },
              { step: '3', title: 'Abrechnung erstellen', desc: 'Per Klick zur fertigen Abrechnung als PDF – rechtssicher und professionell.' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-emerald-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Transparente Preise, keine versteckten Kosten
            </h2>
            <p className="text-lg text-slate-600">
              Wählen Sie das Paket, das zu Ihnen passt. Jederzeit kündbar.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <Card 
                key={index} 
                className={`relative border-2 ${
                  plan.highlight 
                    ? 'border-emerald-500 shadow-xl scale-105' 
                    : 'border-slate-200'
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">
                      {plan.badge}
                    </Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-slate-900">{plan.price}€</span>
                    <span className="text-slate-500 ml-2">{plan.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8 text-left">
                    {plan.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/register">
                    <Button 
                      variant={plan.ctaVariant}
                      className={`w-full ${plan.highlight ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                    >
                      {plan.cta}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-3xl sm:text-4xl font-bold text-white mb-6"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Bereit, Zeit und Geld zu sparen?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Starten Sie jetzt kostenlos und erstellen Sie Ihre erste Nebenkostenabrechnung 
            in weniger als 30 Minuten.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white w-full sm:w-auto">
                Kostenlos starten
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-slate-800 w-full sm:w-auto">
                Anmelden
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="h-6 w-6 text-emerald-600" />
                <span className="font-bold text-lg text-slate-900">Kivera</span>
              </div>
              <p className="text-sm text-slate-500">
                Immobilienverwaltung, die mitdenkt.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 mb-4">Produkt</h4>
              <ul className="space-y-2 text-sm text-slate-500">
                <li><a href="#features" className="hover:text-slate-900">Funktionen</a></li>
                <li><a href="#pricing" className="hover:text-slate-900">Preise</a></li>
                <li><Link to="/roadmap" className="hover:text-slate-900">Roadmap & Changelog</Link></li>
                <li><Link to="/register" className="hover:text-slate-900">Registrieren</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 mb-4">Rechtliches</h4>
              <ul className="space-y-2 text-sm text-slate-500">
                <li><Link to="/impressum" className="hover:text-slate-900" data-testid="footer-impressum">Impressum</Link></li>
                <li><Link to="/datenschutz" className="hover:text-slate-900" data-testid="footer-datenschutz">Datenschutz</Link></li>
                <li><Link to="/agb" className="hover:text-slate-900" data-testid="footer-agb">AGB</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 mb-4">Kontakt</h4>
              <ul className="space-y-2 text-sm text-slate-500">
                <li>kontakt@kivera.de</li>
                <li>SideQuestGuy.com</li>
                <li>Dresden</li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-slate-200 text-center text-sm text-slate-500">
            © {new Date().getFullYear()} Kivera. Alle Rechte vorbehalten.
          </div>
        </div>
      </footer>
    </div>
  );
}
